import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CourierUserApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { getCourier } from '../../state/courierSlice';
import { login } from '../../state/authSlice';
import { showErrorToast, showSuccessToast } from '../../utils/toast';

export const useWhatsAppAuth = () => {
  const dispatch = useDispatch();
  const courier = useSelector(getCourier);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  const requestWhatsAppVerification = async (phoneNumber: string) => {
    if (!courier?.realm) {
      showErrorToast({
        title: 'Error',
        message: 'Courier configuration not found',
      });
      return { success: false, userId: null };
    }

    try {
      setIsSendingCode(true);
      const authApi = new CourierUserApi(apiConfig());

      // Preserve the country code: keep "+" and digits only (E.164), so the
      // backend can route to the correct country (e.g. UK +44 vs NANP +1).
      const formattedPhone = phoneNumber.replace(/[^\d+]/g, '');

      const response = await authApi.courierUserRequestWhatsAppVerification({
        realm: courier.realm,
        phoneNumber: formattedPhone,
      });

      if (response.success) {
        showSuccessToast({
          title: 'Success',
          message: response.message,
        });
        return { success: true, userId: response.userId.toString() };
      } else {
        showErrorToast({
          title: 'Error',
          message: response.message || 'Failed to send verification code',
        });
        return { success: false, userId: null };
      }
    } catch (error: any) {
      console.error('Failed to send WhatsApp code:', error);
      showErrorToast({
        title: 'Error',
        message:
          error.response?.data?.error?.message ||
          'Failed to send verification code',
      });
      return { success: false, userId: null };
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyWhatsAppCode = async (code: string, userId: string) => {
    if (!courier?.realm) {
      showErrorToast({
        title: 'Error',
        message: 'Courier configuration not found',
      });
      return { success: false };
    }

    try {
      setIsLoading(true);
      const authApi = new CourierUserApi(apiConfig());

      const response = await authApi.courierUserLoginWithWhatsAppCode({
        realm: courier.realm,
        code: code,
        userId: userId,
      });

      if (response.success) {
        // Dispatch login with the access token
        dispatch(
          login({
            id: response.accessToken.id,
            ttl: response.accessToken.ttl,
            created: response.accessToken.created,
            userId: response.accessToken.userId,
            user: response.user,
          })
        );
        showSuccessToast({
          title: 'Success',
          message: response.message,
        });
        return { success: true };
      } else {
        showErrorToast({
          title: 'Error',
          message: response.message || 'Failed to verify code',
        });
        return { success: false };
      }
    } catch (error: any) {
      console.error('Failed to verify WhatsApp code:', error);
      showErrorToast({
        title: 'Error',
        message:
          error.response?.data?.error?.message || 'Failed to verify code',
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isSendingCode,
    requestWhatsAppVerification,
    verifyWhatsAppCode,
  };
};
